'use client'

/**
 * Thin storefront wrapper around shared-ui's FeaturedProductsSection that wires
 * the consent-gated analytics callback. Lives in the storefront (not shared-ui)
 * to keep the dependency direction clean: shared-ui has no analytics import; the
 * storefront glues them — same pattern as AnalyticsContactCta / AnalyticsSiteHeader.
 *
 * Required because ContentSections renders server-side and `track` is a
 * browser-only, consent-gated client function. The dispatcher precomputes each
 * card's serializable href + priceLabel so no functions cross this boundary.
 *
 * Emits section_cta_click on every card click, plus the matching funnel "start"
 * for action CTAs (book_demo → booking_start, request_quote → quote_start).
 * view_product is mid-funnel navigation to the PDP — no "start" event.
 * Fire-and-forget; never preventDefault (the Link navigation must proceed).
 */

import { FeaturedProductsSection, type FeaturedProductCardView } from '@sifly/shared-ui/content'
import { track } from '@/lib/analytics'

const START_EVENT: Partial<Record<FeaturedProductCardView['cta'], 'booking_start' | 'quote_start'>> = {
  book_demo: 'booking_start',
  request_quote: 'quote_start',
}

export function AnalyticsFeaturedProducts(props: {
  heading?: string
  items: FeaturedProductCardView[]
}) {
  return (
    <FeaturedProductsSection
      {...props}
      onItemClick={(item) => {
        track('section_cta_click', {
          kind: 'featured_products',
          cta: item.cta,
          slug: item.slug,
          source: item.source,
        })
        const start = START_EVENT[item.cta]
        if (start) track(start, { kind: 'featured_products', slug: item.slug, source: item.source })
      }}
    />
  )
}
